import * as React from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { cn } from "../../lib/utils";

export function NestedSelect({
  options,
  placeholder = "Select an option",
  value,
  onChange,
  className,
}) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [expandedItems, setExpandedItems] = React.useState(new Set());
  const inputRef = React.useRef(null);
  const dropdownRef = React.useRef(null);

  // Find the selected option label
  const findOptionLabel = (options, value) => {
    for (const option of options) {
      if (option.id === value) {
        return option.label;
      }
      if (option.children) {
        const label = findOptionLabel(option.children, value);
        if (label) return label;
      }
    }
    return undefined;
  };

  const selectedLabel = value ? findOptionLabel(options, value) : undefined;

  // Toggle expanded state for an item
  const toggleExpanded = (id, event) => {
    event.stopPropagation();
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Flatten options for search
  const flattenOptions = (options) => {
    return options.reduce((acc, option) => {
      acc.push(option);
      if (option.children) {
        acc.push(...flattenOptions(option.children));
      }
      return acc;
    }, []);
  };

  // Filter options based on search query
  const filteredOptions = React.useMemo(() => {
    if (!searchQuery) return options;

    const flatOptions = flattenOptions(options);
    const filtered = flatOptions.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // For search results, we'll show a flat list
    if (searchQuery) {
      return filtered;
    }

    return options;
  }, [searchQuery, options]);

  // Handle click outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle select option
  const handleSelectOption = (option) => {
    if (Array.isArray(option.children) && option.children.length > 0) {
      return;
    }

    onChange?.(option);
    setSearchQuery("");
    setOpen(false);
  };

  // Render nested options
  const renderNestedOptions = (options, level = 0) => {
    return options.map((option) => {
      const hasChildren = option.children && option.children.length > 0;
      const isExpanded = expandedItems.has(option.id);
      const isSelected = option.id === value;

      return (
        <React.Fragment key={option.id}>
          <div
            className={cn(
              "py-2 text-sm flex items-center",
              isSelected && "bg-blue-500 text-white ",
              option.id.startsWith("E") && !isSelected && "bg-gray-50",
              option.children ? "cursor-default" : "cursor-pointer",
              isSelected === value ? "hover:bg-blue-600" : "hover:bg-blue-100 "
            )}
            style={{
              paddingLeft: `${12 + level * 16}px`,
              paddingRight: "12px",
            }}
            onClick={() => handleSelectOption(option)}
          >
            {hasChildren && (
              <button
                className="mr-1 focus:outline-none"
                onClick={(e) => toggleExpanded(option.id, e)}
              >
                {isExpanded ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                )}
              </button>
            )}
            {!hasChildren && <span className="w-4 mr-1" />}
            <span className="ml-2">{option.label}</span>
          </div>

          {hasChildren &&
            isExpanded &&
            renderNestedOptions(option.children, level + 1)}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="relative w-full">
      <div
        className={cn(
          "relative flex items-center w-full border rounded-md h-12 px-3 py-2 text-sm bg-white",
          "focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500",
          open ? "border-blue-500" : "border-gray-300",
          className
        )}
        onClick={() => {
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        ref={inputRef}
      >
        <input
          type="text"
          className="flex-grow bg-transparent outline-none placeholder:text-gray-500"
          placeholder={selectedLabel || placeholder}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        <ExpandMoreIcon
          className={cn("h-4 w-4")}
          fontSize="medium"
          style={{ color: "#CCCCCC" }}
        />
      </div>

      {open && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              No results found
            </div>
          ) : searchQuery ? (
            filteredOptions.map((option) => (
              <div
                key={option.id}
                className={cn(
                  "px-3 py-2 text-sm cursor-pointer",
                  option.id === value && "bg-blue-500 text-white ",
                  option.id.startsWith("E") && "bg-gray-50",
                  option.id === value
                    ? "hover:bg-blue-600"
                    : "hover:bg-gray-100"
                )}
                onClick={() => handleSelectOption(option)}
              >
                {option.label}
              </div>
            ))
          ) : (
            renderNestedOptions(filteredOptions, 1)
          )}
        </div>
      )}
    </div>
  );
}
